import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Skeleton, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { HCP_REACH_CHART_OPTIONS, HCP_REACH_COLORS } from './constants';

const SLOT_WIDTH = 64;

const percent = (num, den) => (den > 0 ? Math.round((num / den) * 100) : 0);

// Flattens rows into one slot per segment, grouped by month, with an empty
// spacer slot between months so each month reads as its own cluster.
const buildSlots = (rows) => {
    const months = [];
    // a segment is left out when its reach % and engaged % are both 0, even if
    // a stray count came through with them
    const activeRows = rows.filter(
        (row) => row.reachPercentage > 0 || row.successfullyEngagedPercentage > 0
    );
    activeRows.forEach((row) => {
        if (!months.includes(row.month)) months.push(row.month);
    });
    const slots = [];
    months.forEach((month, monthIndex) => {
        if (monthIndex > 0) slots.push({ spacer: true });
        const monthRows = activeRows.filter((row) => row.month === month);
        monthRows.forEach((row, rowIndex) => {
            const total = Math.max(row.totalTargetHCPs, row.reach);
            const reach = Math.min(row.reach, total);
            const engaged = Math.min(row.successfullyEngaged, reach);
            slots.push({
                spacer: false,
                month,
                segment: row.segment,
                showMonth: rowIndex === Math.floor((monthRows.length - 1) / 2),
                total,
                reach,
                engaged,
                reachPct: row.reachPercentage || percent(reach, total),
                engagedPct: row.successfullyEngagedPercentage || percent(engaged, reach)
            });
        });
    });
    return slots;
};

const HcpReachEngagement = () => {
    const { isLoading, hcpReachEngagement } = useSelector(
        (state) => state.digitalEngagement
    );
    const rows = Array.isArray(hcpReachEngagement) ? hcpReachEngagement : [];

    // segment target-list sizes, one entry per segment in API order
    const segmentTotals = useMemo(() => {
        const totals = [];
        rows.forEach((row) => {
            if (!totals.some((item) => item.segment === row.segment)) {
                totals.push({ segment: row.segment, total: row.totalTargetHCPs });
            }
        });
        return totals;
    }, [rows]);

    const options = useMemo(() => {
        const slots = buildSlots(rows);
        if (!slots.some((slot) => !slot.spacer)) return null;

        const maxTotal = Math.max(...slots.map((slot) => (slot.spacer ? 0 : slot.total)));
        // hide counts on slices too short to hold the text
        const MIN_LABEL_SHARE = 0.04;
        const minLabelValue = maxTotal * MIN_LABEL_SHARE;
        const pointData = (valueOf, labelOf) =>
            slots.map((slot) => {
                if (slot.spacer) return { y: null };
                return {
                    y: valueOf(slot),
                    dataLabels: { enabled: valueOf(slot) >= minLabelValue },
                    label: labelOf(slot),
                    slot
                };
            });

        const innerLabel = (color) => ({
            color,
            formatter() {
                return this.point.label;
            }
        });

        return {
            ...HCP_REACH_CHART_OPTIONS,
            chart: {
                ...HCP_REACH_CHART_OPTIONS.chart,
                // keep every slot wide enough for its label; scroll when there are many months
                scrollablePlotArea: {
                    minWidth: slots.length * SLOT_WIDTH,
                    scrollPositionX: 1
                }
            },
            xAxis: {
                ...HCP_REACH_CHART_OPTIONS.xAxis,
                categories: slots.map((slot) => (slot.spacer ? '' : slot.segment)),
                labels: {
                    ...HCP_REACH_CHART_OPTIONS.xAxis.labels,
                    formatter() {
                        const slot = slots[this.pos];
                        if (!slot || slot.spacer) return '';
                        return `<div style="text-align:center">${slot.segment}${
                            slot.showMonth
                                ? `<br/><b style="color:#344054">${slot.month}</b>`
                                : '<br/>&nbsp;'
                        }</div>`;
                    }
                }
            },
            yAxis: {
                ...HCP_REACH_CHART_OPTIONS.yAxis,
                stackLabels: {
                    ...HCP_REACH_CHART_OPTIONS.yAxis.stackLabels,
                    formatter() {
                        const slot = slots[this.x];
                        if (!slot || slot.spacer) return '';
                        return `R ${slot.reachPct}%<br/>E ${slot.engagedPct}%`;
                    }
                }
            },
            tooltip: {
                useHTML: true,
                backgroundColor: '#000',
                style: { color: '#fff', fontSize: '10px' },
                formatter() {
                    const { slot } = this.point;
                    if (!slot) return false;
                    return `<b>${slot.segment} · ${slot.month}</b><br/>
                        Target list: ${slot.total.toLocaleString()}<br/>
                        Reached: ${slot.reach.toLocaleString()} (${slot.reachPct}%)<br/>
                        Successfully engaged: ${slot.engaged.toLocaleString()} (${slot.engagedPct}%)<br/>
                        Not reached: ${(slot.total - slot.reach).toLocaleString()}`;
                }
            },
            series: [
                {
                    name: 'Not reached',
                    color: HCP_REACH_COLORS.notReached,
                    data: pointData((slot) => slot.total - slot.reach, () => ''),
                    dataLabels: { enabled: false },
                    index: 0,
                    legendIndex: 2
                },
                {
                    name: 'Reached, not engaged',
                    color: HCP_REACH_COLORS.reached,
                    data: pointData(
                        (slot) => slot.reach - slot.engaged,
                        (slot) => slot.reach.toLocaleString()
                    ),
                    dataLabels: innerLabel('#344054'),
                    index: 1,
                    legendIndex: 1
                },
                {
                    name: 'Successfully engaged',
                    color: HCP_REACH_COLORS.engaged,
                    data: pointData(
                        (slot) => slot.engaged,
                        (slot) => slot.engaged.toLocaleString()
                    ),
                    dataLabels: innerLabel('#FFFFFF'),
                    index: 2,
                    legendIndex: 0
                }
            ]
        };
    }, [rows]);

    return (
        <Box className="container-p-24-br-16 bg-gray-50">
            <Box>
                <Typography className="text-md-medium gray-900">
                    HCP Reach and Engagement
                </Typography>
                <Typography sx={{ color: '#667085', fontSize: '12px' }}>
                    {segmentTotals.length > 0
                        ? `Bars ${segmentTotals.map((item) => item.segment).join(' / ')} = segment target lists (${segmentTotals.map((item) => item.total.toLocaleString()).join(' / ')})`
                        : ''}
                </Typography>
            </Box>
            {isLoading ? (
                <Skeleton height={400} variant="rectangular" width="100%" />
            ) : options ? (
                <HighchartsReact highcharts={Highcharts} options={options} />
            ) : (
                <Box sx={{
                    height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' 
                }}
                >
                    <Typography className="gray-900" sx={{ fontSize: '14px' }}>
                        No data to display
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default HcpReachEngagement;
